import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/use-local-storage';

/**
 * User preferences persisted client-side.
 *
 * NOTE: This currently uses localStorage. When a backend user-settings table
 * becomes available, the implementation of `useUserPreference` can be swapped
 * to fetch/mutate via the API while preserving the same hook signature.
 */
export type MyWorkordersView = 'day' | 'week' | 'month';

export interface UserPreferences {
  /** When true, timeline is shown chronologically (oldest first) and the
   *  message composer is rendered at the bottom. Default: false. */
  reverseTimelineOrder: boolean;
  /** View mode for the My Workorders screen. Default: 'week'. */
  myWorkordersView: MyWorkordersView;
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  reverseTimelineOrder: false,
  myWorkordersView: 'week',
};

const STORAGE_KEY = 'user-preferences';

/**
 * Read/write a single user preference value.
 *
 * Returns a tuple `[value, setValue]` similar to `useState`.
 */
export function useUserPreference<K extends keyof UserPreferences>(
  key: K,
): [UserPreferences[K], (value: UserPreferences[K]) => void] {
  const [prefs, setPrefs] = useLocalStorage<UserPreferences>(
    STORAGE_KEY,
    DEFAULT_USER_PREFERENCES,
  );

  const value = prefs?.[key] ?? DEFAULT_USER_PREFERENCES[key];

  const setValue = useCallback(
    (next: UserPreferences[K]) => {
      setPrefs((current: UserPreferences) => ({
        ...DEFAULT_USER_PREFERENCES,
        ...current,
        [key]: next,
      }));
    },
    [key, setPrefs],
  );

  return [value, setValue];
}
